import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CategorySection, CategoryTitle, CategoriesGroup, NumberInput, TextInput, ErrorMessage } from './styles.js';
import FormSelect from '../../../commons/FormSelect';
import { getAdditionalCategories } from '../../../../ducks/extras/selectors';
import { transformValue } from '../../../../utils/numberFormatHelper.js';

const AdditionalCategories = ({
  device,
  selectFeatures,
  inputFeatures,
  onSelectChange,
  onInputChange,
  register,
  errors
}) => {
  const { t } = useTranslation();
  const allFeatures = useSelector(getAdditionalCategories);

  const handleNumChange = (e, featureId, maxDigits) => {
    const value = e.target.value;
    // Permitimos solo dígitos y se limita a maxDigits
    let cleanValue = value.replace(/[^0-9]/g, '');
    // Eliminamos los ceros iniciales, pero preservamos un único cero
    if (cleanValue.length > 1 && cleanValue.startsWith('0')) {
      cleanValue = cleanValue.replace(/^0+/, '');
    }
    if (cleanValue.length > maxDigits) {
      cleanValue = cleanValue.slice(0, maxDigits);
    }
    // Actualizamos directamente el estado local
    onInputChange(featureId, cleanValue);
  };

  const handleTextChange = (e, featureId) => {
    const value = e.target.value;
    // Actualizamos directamente el estado local
    onInputChange(featureId, value);
  };

  const getErrorMessage = errorMessage => {
    // Para mensajes de rango numérico
    if (errorMessage.includes('professionalPubli.extras.error.Num:')) {
      const [baseKey, minVal, maxVal] = errorMessage.split(':');
      return t(baseKey, { value1: minVal, value2: maxVal });
    }

    // Para otros mensajes de error
    return t(errorMessage);
  };

  const getFeatureType = feature => {
    const isSelect = feature.fieldType === 'Select';
    const featureState = isSelect
      ? selectFeatures.find(f => f.id === feature.id)
      : inputFeatures.find(f => f.id === feature.id);

    if (isSelect) {
      return (
        <div data-publi='selects-categories' key={feature.id}>
          <FormSelect
            label={feature.name}
            options={feature.options || []}
            value={featureState?.value || ''}
            onChange={value => onSelectChange(feature.id, value)}
            placeholder={featureState?.value || t('professionalPubli.extras.placeholder.Select')}
            device={device}
            resetOptionLabel={t('professionalPubli.extras.placeholder.Select')}
          />
        </div>
      );
    }

    if ((feature.fieldType === 'Num' || feature.fieldType === 'TextNum') && !!feature.name) {
      const minVal = feature.minValidation ?? '0';
      const maxVal = feature.maxValidation ?? '10';
      const maxValStr = String(maxVal);
      const maxDigits = feature.fieldType === 'Num' ? maxValStr.length : maxValStr.length + 1;
      const currentValue = featureState?.value || '';

      const getPlaceholder = () => {
        if (feature.fieldType === 'TextNum') {
          return t('professionalPubli.extras.placeholder.TextNum', { value: minVal });
        }
        return t('professionalPubli.extras.placeholder.Num', { value1: minVal, value2: maxVal });
      };

      return (
        <NumberInput data-publi='inputs-categories' key={feature.id}>
          <label data-publi={`input-categorie-${feature.id}`} htmlFor={feature.id}>
            {feature.name}
          </label>
          <input
            type='text'
            pattern='[0-9]*'
            maxLength={maxDigits}
            className={errors[feature.id] ? 'error' : ''}
            value={transformValue(currentValue)}
            name={feature.id}
            id={feature.id}
            {...register(feature.id)}
            onChange={e => handleNumChange(e, feature.id, maxDigits)}
            placeholder={getPlaceholder()}
          />
          {errors[feature.id] && <ErrorMessage>{getErrorMessage(errors[feature.id].message)}</ErrorMessage>}
        </NumberInput>
      );
    }

    // Para campos de tipo Text
    const maxVal = feature.maxValidation || null;
    const maxDigits = parseInt(maxVal) + 1;
    const currentValue = featureState?.value || '';

    return (
      <React.Fragment key={feature.id}>
        {!!feature.name && (
          <TextInput data-publi='texts-categories'>
            <label data-publi={`text-categorie-${feature.id}`} htmlFor={feature.id}>
              {feature.name}
            </label>
            <input
              type='text'
              maxLength={maxDigits}
              className={errors[feature.id] ? 'error' : ''}
              value={currentValue}
              name={feature.id}
              id={feature.id}
              {...register(feature.id)}
              onChange={e => handleTextChange(e, feature.id)}
              placeholder={t('professionalPubli.extras.placeholder.Text', { value: feature.name?.toLowerCase() })}
            />
            {errors[feature.id] && <ErrorMessage>{getErrorMessage(errors[feature.id].message)}</ErrorMessage>}
          </TextInput>
        )}
      </React.Fragment>
    );
  };

  return (
    <CategorySection data-publi='additional-categories-section'>
      <CategoryTitle data-publi='additional-categories-title'>
        {t('professionalPubli.extras.category-14')}
      </CategoryTitle>
      <CategoriesGroup device={device}>{allFeatures.map(getFeatureType)}</CategoriesGroup>
    </CategorySection>
  );
};

export default AdditionalCategories;
